/* eslint-disable max-lines-per-function */
import {
  Body,
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@e2n/ui/v2';
import { Beruf } from '@/domain/mitarbeiter/features/rotationsplan/entities/Beruf';
import { BerufeSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateRotationsplan/components/BerufeSelectV2';
import type { useForm as useCreateForm } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateRotationsplanVorlage/useForm';
import { UnsavedChangesDialog } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/UnsavedChangesDialog';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Formular-Sheet einer Vorlage. Das `form` kommt vom Anlegen oder Bearbeiten.
 */
export function RotationsplanVorlageSheet({
  titel,
  berufe,
  form,
  titelVergeben,
  close,
}: {
  titel: string;
  berufe: Beruf[];
  form: ReturnType<typeof useCreateForm>['form'];
  titelVergeben: boolean;
  close: () => void;
}) {
  const { t } = useTranslation();
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  // zuletzt vorbelegter Titel, damit ein selbst geänderter Titel beim Berufswechsel stehen bleibt
  const vorbelegterTitel = useRef('');

  const hasUnsavedChanges = () =>
    Object.values(form.state.fieldMeta).some((field) => !field?.isDefaultValue);

  const berufLabel = t(
    'domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.fields.beruf.label',
  );

  return (
    <>
      <Sheet
        open
        onOpenChange={(open) => {
          if (open) {
            return;
          }

          if (hasUnsavedChanges()) {
            setShowUnsavedChangesDialog(true);
          } else {
            close();
          }
        }}>
        <SheetContent
          aria-describedby={undefined}
          className="data-[side=right]:w-full sm:data-[side=right]:max-w-130">
          <SheetHeader>
            <SheetTitle className="flex-1">{titel}</SheetTitle>
            <form.Subscribe>
              <Button
                className="md:hidden"
                disabled={!form.state.isValid || form.state.isSubmitting}
                form="rotationsplanVorlageForm">
                {t('actions.fertig')}
              </Button>
            </form.Subscribe>
          </SheetHeader>
          <SheetBody className="flex flex-col gap-4 sm:p-6">
            <Body size="sm" className="text-labels-secondary">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.hinweisPflichtfelder',
              )}
            </Body>
            <form
              id="rotationsplanVorlageForm"
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                form.handleSubmit();
              }}>
              <form.Field
                name="beruf"
                listeners={{
                  onChange: ({ value }) => {
                    const titel = form.getFieldValue('titel');

                    if (
                      !value ||
                      (titel.trim() !== '' &&
                        titel !== vorbelegterTitel.current)
                    ) {
                      return;
                    }

                    vorbelegterTitel.current = value.name;
                    form.setFieldValue('titel', value.name);
                  },
                }}>
                {(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;

                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel
                        htmlFor={field.name}
                        invalid={isInvalid}
                        requiredField>
                        {berufLabel}
                      </FieldLabel>
                      <BerufeSelect
                        berufe={berufe}
                        handleChange={field.handleChange}
                        id={field.name}
                        invalid={isInvalid}
                        value={field.state.value}
                      />
                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              </form.Field>
              <form.Field name="titel">
                {(field) => {
                  const isInvalid =
                    titelVergeben ||
                    (field.state.meta.isTouched && !field.state.meta.isValid);

                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel
                        htmlFor={field.name}
                        invalid={isInvalid}
                        requiredField>
                        {t(
                          'domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.fields.titel.label',
                        )}
                      </FieldLabel>
                      <Input
                        id={field.name}
                        aria-invalid={isInvalid}
                        value={field.state.value}
                        maxLength={100}
                        onBlur={field.handleBlur}
                        onChange={(event) =>
                          field.handleChange(event.target.value)
                        }
                        placeholder={t(
                          'domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.fields.titel.placeholder',
                        )}
                      />
                      {isInvalid && (
                        <FieldError
                          errors={
                            titelVergeben
                              ? [
                                  {
                                    message: t(
                                      'domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.fields.titel.vergeben',
                                    ),
                                  },
                                ]
                              : field.state.meta.errors
                          }
                        />
                      )}
                    </Field>
                  );
                }}
              </form.Field>
            </form>
          </SheetBody>
          <SheetFooter className="hidden md:flex">
            <form.Subscribe>
              <Button
                disabled={!form.state.isValid || form.state.isSubmitting}
                form="rotationsplanVorlageForm">
                {t('actions.fertig')}
              </Button>
            </form.Subscribe>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <UnsavedChangesDialog
        open={showUnsavedChangesDialog}
        onCancel={() => setShowUnsavedChangesDialog(false)}
        onConfirm={() => {
          form.reset();
          setShowUnsavedChangesDialog(false);
          close();
        }}
      />
    </>
  );
}
